export const PIPELINE_HEALTH_L2_PATH = "/revenue-intelligence/state-of-sales-v2/pipeline-health-l2";

export const PIPELINE_HEALTH_L2_COPY = {
  title: "Pipeline Health",
  subtitle: "What supports target, and what still has to happen?",
  changed: "What changed?",
  back: "Back to Pipeline Health",
  expected: "Expected sales contribution",
  rawValue: "Deal value",
  coveragePeriod: "Coverage period",
  lossScope: "Losses in the last 30 days · independent of Coverage period",
} as const;

export const PIPELINE_HEALTH_OVERVIEW_COPY = {
  coverageLink: "Inspect target coverage",
  creationLink: "Inspect creation and growth plan",
  leakageLink: "Inspect pre-loss signals",
  expectedContribution: "Future opportunities are excluded from this figure.",
  creationHistoryPending: "The plan, new-business scope and current-value reporting basis are set. The available cohort, currency and timing inputs determine whether Required can be calculated.",
  creationHistory: "Historical creation pace, not a future-target requirement.",
  noFurtherChange: "No additional material change is established by comparable history.",
  noFurtherAction: "No additional intervention is established by the current assessment.",
  sustainCreation: "Deliver the creation assumed in the outlook",
  baselineVolume: "Baseline volume, not an additional pipeline requirement.",
} as const;

export const CREATION_PLAN_COPY = {
  title: "Pipeline Creation — Actual vs Required",
  window: (from: string, to: string) => `Monthly creation · ${from}–${to} · independent of Coverage period`,
  horizon: "Outlook through",
  countTitle: "Opportunities created",
  gated: "A provisional ARR plan is ready; monetary creation sufficiency still needs verified inputs.",
  countScope: "Retained CRM opportunities · all business types · selected filters. Counts are not new ARR or a target-based requirement.",
  partial: "Current month is partial; no extrapolation or shortfall judgement.",
  structural: "Required is a structural monthly creation rate. Current-quarter wins and Forecast do not reduce it; accumulated shortfalls do not increase it.",
  planTitle: "Growth plan",
  provisional: "Provisional planning scenario · editable locally · awaiting customer confirmation",
  confirmed: "Local changes are not saved to customer records.",
  calculationBasis: "Calculation basis",
  history: "Historical cohort assumptions, not predictive model outputs",
  leadIn: "Lead-in creation is a modeled reference before the starting ARR date, not a reconstructed historical target.",
  reference: "Required · modeled plan reference",
  referenceScope: (month: string) => `Before ${month}, Required is a modeled plan reference, not a historical target.`,
} as const;

export const CREATION_EXPLORATION_COPY = {
  title: "Pipeline creation against the growth plan",
  window: CREATION_PLAN_COPY.window,
  horizon: CREATION_PLAN_COPY.horizon,
  reference: CREATION_PLAN_COPY.reference,
  referenceScope: CREATION_PLAN_COPY.referenceScope,
  valueTitle: "Pipeline value created",
  newArrTitle: "New ARR pipeline created",
  countTitle: "Number of deals created",
  required: "Required to reach",
  fullMonthRequired: "Required · full month",
  noSeasonality: "No seasonal adjustment.",
  inspect: "Inspect plan and drivers",
  countDefinition: "New opportunities created each month.",
  realDefinition: "Gross pipeline value at today's deal values — not weighted value or ending ARR.",
  currentValueBasis: "Approved reporting basis: today's saved ARR value is attributed to the original creation month, including later value changes. These are not value-at-creation snapshots; no size-change events are invented on the timeline.",
  filtered: "Required is hidden for a filtered view; the growth target is company-wide.",
  countOnly: "Required is hidden in number-of-deals view; the growth plan is value-based.",
  partial: "The current month is partial. Future months show Required only.",
  actualOnly: "The current month is partial; no extrapolation or target-gap assessment.",
  changed: PIPELINE_HEALTH_L2_COPY.changed,
  defaultPipelines: "New Customer + Tender (default)",
  allPipelines: "All business types",
  productLabelsPending: "Product labels are not included in the saved CRM snapshot.",
  productLabelsLoading: "Reading product labels from CRM…",
  productLabelsError: "Product labels could not be read. Saved values and deal counts remain available.",
  productLabelsLive: "Product labels read live from CRM; ARR values from saved records.",
  estimatedProduct: "Estimated product allocation",
  productValue: "Product ARR share",
  productRecords: "Multi-product deal values are split equally, not observed product prices. Deal counts remain unique.",
} as const;

export const PIPELINE_HEALTH_ANALYSES = {
  coverage: { label: "Coverage", question: "Do we have enough?" },
  "pipeline-creation": { label: "Pipeline Creation", question: "Are we building enough?" },
  leakage: { label: "Leakage", question: "Where are we losing value?" },
} as const;

export type PipelineHealthAnalysis = keyof typeof PIPELINE_HEALTH_ANALYSES;

export function getPipelineHealthAnalysis(path: string): PipelineHealthAnalysis | null {
  const key = path.split("?")[0].split("/").at(-1);
  return key && Object.hasOwn(PIPELINE_HEALTH_ANALYSES, key) ? key as PipelineHealthAnalysis : null;
}

export function pipelineHealthPath(analysis: PipelineHealthAnalysis | null, query: string) {
  return `${PIPELINE_HEALTH_L2_PATH}${analysis ? `/${analysis}` : ""}?${query}`;
}
